import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private sessionTimeoutId: ReturnType<typeof setTimeout> | null = null;
  
  private loggedIn = new BehaviorSubject<boolean>(this.hasValidToken());
  isLoggedIn$ = this.loggedIn.asObservable();

  constructor(private http: HttpClient, private router: Router) {}

  private hasValidToken(): boolean {
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }

    const expirationTime = this.getTokenExpirationTime(token);

    if (!expirationTime) {
      return true;
    }

    return expirationTime > Date.now();
  }

  initializeSessionMonitor(): void {
    const token = localStorage.getItem('token');

    if (!token) {
      this.clearSessionTimeout();
      this.loggedIn.next(false);
      return;
    }

    const expirationTime = this.getTokenExpirationTime(token);

    if (!expirationTime) {
      this.loggedIn.next(true);
      return;
    }

    if (expirationTime <= Date.now()) {
      this.handleExpiredSession();
      return;
    }

    this.loggedIn.next(true);
    this.scheduleSessionTimeout(expirationTime);
  }

  private scheduleSessionTimeout(expirationTime: number): void {
    this.clearSessionTimeout();

    const delay = expirationTime - Date.now();

    if (delay <= 0) {
      this.handleExpiredSession();
      return;
    }

    this.sessionTimeoutId = setTimeout(() => {
      this.handleExpiredSession();
    }, delay);
  }

  private clearSessionTimeout(): void {
    if (this.sessionTimeoutId !== null) {
      clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = null;
    }
  }

  private handleExpiredSession(): void {
    this.clearSessionTimeout();
    localStorage.removeItem('token');
    this.loggedIn.next(false);

    if (this.router.url !== '/login') {
      void this.router.navigate(['/login']);
    }
  }

  private getTokenExpirationTime(token: string): number | null {
    try {
      const payload = token.split('.')[1];

      if (!payload) {
        return null;
      }

      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');
      const decodedPayload = JSON.parse(atob(paddedPayload));

      if (typeof decodedPayload.exp !== 'number') {
        return null;
      }

      return decodedPayload.exp * 1000;
    } catch {
      return null;
    }
  }

  login(email: string, password: string) {
    const credentials = { email, password };
    return this.http.post(environment.apiUrl + '/api/auth/login', credentials, { responseType: 'text' });
  }

  setToken(token: string) {
    localStorage.setItem('token', token);
    this.loggedIn.next(true);

    const expirationTime = this.getTokenExpirationTime(token);
    if (expirationTime) {
      this.scheduleSessionTimeout(expirationTime);
    }
  }

  logout() {
    this.clearSessionTimeout();
    localStorage.clear();
    this.loggedIn.next(false);
    if (this.router.url !== '/login') {
      void this.router.navigate(['/login']);
    }
  }

  isLoggedIn(): boolean {
    const isValid = this.hasValidToken();

    if (!isValid && localStorage.getItem('token')) {
      this.handleExpiredSession();
    }

    return isValid;
  }

  setLoggedIn(isLoggedIn : boolean){
    this.loggedIn.next(isLoggedIn);
  }

  recoveryPassword(email : string){
    return this.http.post(environment.apiUrl + '/api/auth/recovery-password', email, { responseType: 'text' });
  }

  verifyToken(token : string){
    return this.http.post<boolean>(environment.apiUrl + '/api/auth/verify-token', token);
  }

  resetPassword(token : string, password : string){
    const body = {password,token}
    return this.http.post(environment.apiUrl + '/api/auth/reset-password', body);
  }

}
